import React, { Fragment, useMemo, useRef } from "react";
import { FormattedMessage } from "react-intl";
import PropTypes from "prop-types";
import _ from "lodash";

import classnames from "classnames";
import { Mobile, NotMobile } from "../../hocs/Responsive";
import { Select } from "../select";
import { ProfilesNativeSelect } from "../profiles/ProfilesNativeSelect";

import { Input } from "../input/Input";
import {
  isDeviceAddressExist,
  isDeviceNameBusy,
  isDeviceNameValid,
  isMacValid,
  isUserNameValid,
} from "../../helpers/validation.helper";
import ButtonLink from "../buttonLink/ButtonLink";
import { Icon } from "../icon/Icon";
import { formatProfileNameInHook } from "../profiles/FormattedProfileName";
import { useIntlTranslate } from "../../hooks";

import "./commonStyles.scss";

export const ProfileSelect = ({
  profiles,
  profile,
  handleChange,
  className,
}) => {
  const translate = useIntlTranslate();

  const items = useMemo(() => {
    return profiles.map(({ value, name, isDisabled }) => ({
      value,
      content: formatProfileNameInHook(name, translate),
      disabled: isDisabled,
    }));
  }, [profiles, translate]);

  return (
    <Fragment>
      {profiles.length !== 1 ? (
        <Fragment>
          <NotMobile>
            <Select
              items={items}
              selected={profile}
              onChange={(selected) => handleChange(selected)}
              label={translate("device_group")}
            />
          </NotMobile>
          <Mobile>
            <ProfilesNativeSelect
              id="device-group-select"
              profiles={profiles}
              selected={profile}
              onChange={handleChange}
              label="device_group"
            />
          </Mobile>
        </Fragment>
      ) : (
        <div className={`${className}__select-stub`}>
          <div className={`${className}__select-stub__label`}>
            <FormattedMessage id={"device_group"} />
          </div>
          <div className={`${className}__select-stub__text`}>
            <FormattedMessage id={profiles[0].name} />
          </div>
        </div>
      )}
    </Fragment>
  );
};

export const IdentifiersStub = ({
  value,
  className,
  label = "device_id",
  id = "device-id",
}) => {
  return (
    <Input
      className={`${className}__identifiers-stub`}
      id={id}
      value={value}
      label={<FormattedMessage id={label} />}
      onChange={_.noop}
      readOnly
    />
  );
};

export const IdentifiersStubForUnmerge = ({
  className,
  value,
  label,
  removed,
  disabled,
  onClick,
  deviceName,
}) => {
  const translate = useIntlTranslate();
  const iconClass = removed ? "fa-undo color-primary" : "fa-times color-danger";
  const classes = classnames(
    {
      [`${className}__identifiers--disabled`]: removed,
    },
    `${className}__identifiers`
  );

  const ariaLabel = translate(removed ? "aria_undo_unmerge" : "aria_unmerge", {
    deviceName,
    type: translate(label),
  });

  return (
    <li className={classes}>
      <span className={`${className}__identifiers-title`}>
        <FormattedMessage id={label} />:{" "}
      </span>
      <span className={`${className}__identifiers-content`}>{value}</span>
      <ButtonLink disabled={disabled} aria-label={ariaLabel} onClick={onClick}>
        <Icon icon={`fa-solid ${iconClass}`} />
      </ButtonLink>
    </li>
  );
};

export const DeviceNameInput = ({
  name,
  error,
  handleChange,
  devices,
  autoFocus = false,
  allowEmptyName = false,
  label,
}) => {
  const translate = useIntlTranslate();
  const { current: originalName } = useRef(name);
  const filteredDevices = useMemo(
    () => _.filter(devices, (device) => device.name !== originalName),
    [devices, originalName]
  );
  const commonProps = {
    id: "device-name-input",
    error,
    value: name,
    label: label,
    onChange: ({ target: { value } }) =>
      onDeviceNameChange(value, handleChange, filteredDevices, allowEmptyName),
    onClear: () =>
      onDeviceNameChange("", handleChange, filteredDevices, allowEmptyName),
    placeholder: translate("device_name_placeholder"),
    maxLength: 30,
  };

  return (
    <Fragment>
      <Mobile>
        <Input {...commonProps} />
      </Mobile>
      <NotMobile>
        <Input {...commonProps} autoFocus={autoFocus} />
      </NotMobile>
    </Fragment>
  );
};

export const MacAddressInput = ({
  error,
  address = "",
  handleChange,
  autoFocus,
  devices,
  label,
}) => {
  const translate = useIntlTranslate();
  const commonProps = {
    id: "mac-address-input",
    error,
    showLimit: false,
    maxLength: "17",
    autoCorrect: "off",
    value: address,
    label: label,
    onChange: ({ target: { value } }) =>
      onMacAddressChange(value, handleChange, devices),
    onClear: () => onMacAddressChange("", handleChange, devices),
    placeholder: translate("mac_address_placeholder"),
  };

  return (
    <Fragment>
      <Mobile>
        <Input {...commonProps} />
      </Mobile>
      <NotMobile>
        <Input {...commonProps} autoFocus={autoFocus} />
      </NotMobile>
    </Fragment>
  );
};

export const UserFullNameInput = ({
  value,
  error,
  handleChange,
  autoFocus = false,
  allowEmptyName = false,
  label,
}) => {
  const commonProps = {
    id: "user-fullname-input",
    error,
    value,
    label,
    onChange: (e) =>
      onUserFullNameChange(e.target.value, handleChange, allowEmptyName),
    onClear: () => onUserFullNameChange("", handleChange, allowEmptyName),
    maxLength: 30,
  };

  return (
    <Fragment>
      <Mobile>
        <Input {...commonProps} />
      </Mobile>
      <NotMobile>
        <Input {...commonProps} autoFocus={autoFocus} />
      </NotMobile>
    </Fragment>
  );
};

const onDeviceNameChange = (name, handler, devices, allowEmptyName) => {
  const invalidError = isDeviceNameValid(name, allowEmptyName);
  const isNameBusy = isDeviceNameBusy(devices, name);
  const isEmptyString =
    allowEmptyName && name.length > 0 && name.trim().length === 0;

  const error = isEmptyString
    ? "device_name_required"
    : invalidError
    ? invalidError
    : isNameBusy
    ? "device_name_already_exist"
    : "";

  return handler && handler(name, error);
};
const onMacAddressChange = (value, handler, devices) => {
  const address = _.trim(value);
  const invalidError = isMacValid(address);
  const isExist = isDeviceAddressExist(devices, address);
  const error = invalidError
    ? invalidError
    : isExist
    ? "device_already_exist"
    : "";

  return handler && handler(address, error);
};
const onUserFullNameChange = (value, handler, allowEmptyName) => {
  const invalidError = isUserNameValid(value, allowEmptyName);
  const isEmptyString =
    allowEmptyName && value.length > 0 && value.trim().length === 0;

  const error = isEmptyString
    ? "user_name_required"
    : invalidError
    ? invalidError
    : "";

  return handler && handler(value, error);
};

export const InputStub = ({ value, label }) => (
  <Input readOnly value={value} onChange={_.noop} label={label} />
);

ProfileSelect.propTypes = {
  profile: PropTypes.string,
  profiles: PropTypes.array.isRequired,
  className: PropTypes.string.isRequired,
  handleChange: PropTypes.func.isRequired,
};

IdentifiersStub.propTypes = {
  value: PropTypes.string.isRequired,
  label: PropTypes.string,
  className: PropTypes.string,
  id: PropTypes.string,
};

DeviceNameInput.propTypes = {
  error: PropTypes.string,
  name: PropTypes.string.isRequired,
  devices: PropTypes.object.isRequired,
  handleChange: PropTypes.func,
};

MacAddressInput.propTypes = {
  error: PropTypes.string,
  autoFocus: PropTypes.bool,
  address: PropTypes.string,
  devices: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
};

UserFullNameInput.propTypes = {
  error: PropTypes.string,
  value: PropTypes.string.isRequired,
  handleChange: PropTypes.func,
};

InputStub.propTypes = {
  value: PropTypes.string.isRequired,
  handleChange: PropTypes.func,
};
